// Find in document (design §14.2, the Search control in the bottom bar).
//
// Matching is by paragraph rather than by character range: each paragraph is
// its own editing surface, so a paragraph is the smallest thing the editor can
// meaningfully take you to. The row is highlighted and scrolled into view.

import { useCallback, useEffect, useMemo, useState } from "react";

import type { DocState, ParagraphId } from "../store/ops";

export interface Find {
  query: string;
  setQuery: (q: string) => void;
  matches: ParagraphId[];
  index: number;
  current: ParagraphId | null;
  next: () => void;
  previous: () => void;
}

export function useFind(doc: DocState, open: boolean): Find {
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return doc.order.filter((id) => (doc.text[id] ?? "").toLowerCase().includes(needle));
  }, [doc, query]);

  // A new search starts at the first match rather than wherever the last one
  // happened to end.
  useEffect(() => {
    setIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const current = matches.length > 0 ? (matches[index % matches.length] ?? null) : null;

  const step = useCallback(
    (by: number) => {
      if (matches.length === 0) return;
      setIndex((i) => (i + by + matches.length) % matches.length);
    },
    [matches.length],
  );

  return {
    query,
    setQuery,
    matches,
    index: matches.length === 0 ? 0 : index % matches.length,
    current,
    next: () => step(1),
    previous: () => step(-1),
  };
}
